import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Plus, Trash2, Edit2, Wand2, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function AdminMenus() {
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMenu, setEditingMenu] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchMenus = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/menus`, { withCredentials: true });
      setMenus(data);
    } catch (e) {
      toast.error('Error al cargar menús');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/menus/${deleteId}`, { withCredentials: true });
      fetchMenus();
      toast.success('Menú eliminado');
    } catch (e) {
      toast.error('Error al eliminar');
    } finally {
      setDeleteId(null);
    }
  };

  const openEdit = (menu) => {
    if(menu) {
      setEditingMenu({ ...menu });
    } else {
      setEditingMenu({ title: '', description: '', image_url: '', items: [] });
    }
    setIsDialogOpen(true);
  };

  if (loading) return <div>Cargando...</div>;

  const categories = Array.from(new Set(menus.flatMap(m => m.items?.map(i => i.category)).filter(Boolean)));

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-serif text-blue-900 font-bold">Gestión de Menús</h1>
        <Button onClick={() => openEdit()} className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl" data-testid="add-menu-btn">
          <Plus className="mr-2 h-4 w-4" /> Nuevo Menú
        </Button>
      </div>

      <div className="space-y-6">
        {menus.map(menu => (
          <div key={menu._id} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col sm:flex-row gap-6 justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-slate-800">{menu.title}</h2>
              <p className="text-slate-500 mt-1">{menu.description}</p>
              <div className="mt-4 text-sm font-medium text-orange-600 bg-orange-50 inline-block px-3 py-1 rounded-lg">
                {menu.items?.length || 0} platillos
              </div>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <Button variant="outline" onClick={() => openEdit(menu)} className="flex-1 sm:flex-none rounded-xl" data-testid={`edit-menu-${menu._id}`}>
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button variant="destructive" onClick={() => setDeleteId(menu._id)} className="flex-1 sm:flex-none rounded-xl">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-sm rounded-3xl p-6 text-center">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-blue-900">¿Eliminar Menú?</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 my-4">Esta acción no se puede deshacer.</p>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={() => setDeleteId(null)} className="rounded-xl">Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} className="rounded-xl">Sí, eliminar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {isDialogOpen && (
        <MenuEditorDialog 
          menu={editingMenu} 
          categories={categories}
          onClose={() => setIsDialogOpen(false)} 
          onSave={() => { setIsDialogOpen(false); fetchMenus(); }} 
        />
      )}
    </div>
  );
}

function MenuEditorDialog({ menu, categories, onClose, onSave }) {
  const [formData, setFormData] = useState(menu);
  const [saving, setSaving] = useState(false);
  const [uploadingMenuImg, setUploadingMenuImg] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);

  const saveMenu = async () => {
    setSaving(true);
    try {
      if (formData._id) {
        await axios.put(`${API_URL}/api/menus/${formData._id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/menus`, formData, { withCredentials: true });
      }
      toast.success('Menú guardado');
      onSave();
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...(prev.items || []), { id: Date.now().toString(), name: '', description: '', price: '', category: '', image_url: '' }]
    }));
  };

  const updateItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  const removeItem = (id) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  };

  const generateAIdescription = async (id, name, desc) => {
    if (!name) return toast.error('Ingresa el nombre del platillo primero');
    const toastId = toast.loading('Generando con IA...');
    try {
      const { data } = await axios.post(`${API_URL}/api/ai/suggest`, { name, ingredients: desc || name }, { withCredentials: true });
      updateItem(id, 'description', data.description);
      toast.success('Descripción generada', { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error de IA', { id: toastId });
    }
  };

  const handleMenuImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingMenuImg(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, { withCredentials: true });
      setFormData(prev => ({ ...prev, image_url: data.url }));
      toast.success('Imagen del menú subida');
    } catch(err) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingMenuImg(false);
    }
  };

  const handleItemImageUpload = async (e, id) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingItemId(id);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, { withCredentials: true });
      updateItem(id, 'image_url', data.url);
      toast.success('Foto del platillo subida');
    } catch(err) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingItemId(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif text-blue-900">{formData._id ? 'Editar Menú' : 'Nuevo Menú'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="space-y-4 bg-slate-50 p-6 rounded-2xl border border-slate-100">
            <div>
              <Label>Título del Menú (ej. Menú de Cena)</Label>
              <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="mt-1 bg-white" data-testid="menu-title-input" />
            </div>
            <div>
              <Label>Descripción Breve</Label>
              <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="mt-1 bg-white" rows={2} />
            </div>
            <div>
              <Label>Foto del Menú (Opcional, abajo de la descripción)</Label>
              {formData.image_url && (
                <div className="mt-2 mb-3">
                  <img src={formData.image_url.startsWith('http') ? formData.image_url : `${API_URL}${formData.image_url}`} alt="Menu Cover" className="h-32 w-auto object-cover rounded-xl" />
                </div>
              )}
              <div className="flex items-center gap-3">
                 <Input type="file" accept="image/*" onChange={handleMenuImageUpload} disabled={uploadingMenuImg} className="file:bg-orange-50 file:text-orange-700 file:border-0 file:rounded-lg file:px-4 file:py-2 file:font-semibold hover:file:bg-orange-100 cursor-pointer bg-white" />
                 {uploadingMenuImg && <span className="text-sm text-slate-500">Subiendo...</span>}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800">Opciones / Platillos</h3>
              <Button type="button" onClick={addItem} variant="outline" className="rounded-xl border-orange-200 text-orange-700 hover:bg-orange-50" data-testid="add-item-btn">
                <Plus className="h-4 w-4 mr-2" /> Añadir Platillo
              </Button>
            </div>

            <datalist id="category-list">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>

            <div className="space-y-4">
              {formData.items?.map((item, index) => (
                <div key={item.id} className="bg-white border border-slate-200 p-4 sm:p-5 rounded-2xl relative shadow-sm">
                  <button onClick={() => removeItem(item.id)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500">
                    <Trash2 className="h-5 w-5" />
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8">
                    <div>
                      <Label>Nombre del Platillo</Label>
                      <Input value={item.name} onChange={e => updateItem(item.id, 'name', e.target.value)} className="mt-1" placeholder="Ej. Paella de Mariscos" />
                    </div>
                    <div>
                      <Label>Precio (Opcional)</Label>
                      <Input value={item.price} onChange={e => updateItem(item.id, 'price', e.target.value)} className="mt-1" placeholder="Ej. $35" />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="flex justify-between items-end mb-1">
                        <Label>Descripción o Ingredientes</Label>
                        <button type="button" onClick={() => generateAIdescription(item.id, item.name, item.description)} className="text-xs text-orange-600 font-medium flex items-center hover:underline" data-testid={`ai-btn-${index}`}>
                          <Wand2 className="h-3 w-3 mr-1" /> Mejorar con IA
                        </button>
                      </div>
                      <Textarea value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} className="mt-1" rows={2} />
                    </div>
                    <div>
                      <Label>Categoría (Autocompletable)</Label>
                      <Input list="category-list" value={item.category || ''} onChange={e => updateItem(item.id, 'category', e.target.value)} className="mt-1" placeholder="Ej. Principal, Postre..." />
                    </div>
                    <div>
                      <Label>Foto Opcional</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="file" accept="image/*" onChange={(e) => handleItemImageUpload(e, item.id)} disabled={uploadingItemId === item.id} className="text-sm file:bg-slate-100 file:text-slate-700 file:border-0 file:rounded-md file:px-3 file:py-1 hover:file:bg-slate-200" />
                        {uploadingItemId === item.id && <span className="text-xs text-slate-500">Subiendo...</span>}
                      </div>
                      {item.image_url && (
                        <img src={item.image_url.startsWith('http') ? item.image_url : `${API_URL}${item.image_url}`} alt="Item" className="h-16 w-16 object-cover rounded-lg mt-2" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {(!formData.items || formData.items.length === 0) && (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  No hay platillos aún. Añade el primero.
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-3">
            <Button variant="ghost" onClick={onClose} className="rounded-xl">Cancelar</Button>
            <Button onClick={saveMenu} disabled={saving} className="bg-blue-900 hover:bg-blue-800 text-white rounded-xl px-8" data-testid="save-menu-btn">
              {saving ? 'Guardando...' : 'Guardar Menú'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
